// src/AuthContext.js - Admin via custom claims + subscription/access info from Firestore
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);

  // Custom claims
  const [isAdmin, setIsAdmin] = useState(false);

  // Firestore docs
  const [userDoc, setUserDoc] = useState(null);        // users/{uid}
  const [settingsDoc, setSettingsDoc] = useState(null); // users/{uid}/settings/main

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubUser = null;
    let unsubSettings = null;

    const ensureUserSettingsDoc = async (uid) => {
      const settingsRef = doc(db, 'users', uid, 'settings', 'main');
      const snap = await getDoc(settingsRef);
      if (snap.exists()) return;

      await setDoc(
        settingsRef,
        {
          access: 'locked', // preview mode default
          lifetime: false,

          nextInvoiceId: 1,
          nextReportId: 1,

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsAdmin(false);
      setUserDoc(null);
      setSettingsDoc(null);

      if (unsubUser) unsubUser();
      if (unsubSettings) unsubSettings();
      unsubUser = null;
      unsubSettings = null;

      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // Ensure settings doc exists
      try {
        await ensureUserSettingsDoc(user.uid);
      } catch (e) {
        console.error('Failed to ensure settings doc:', e);
      }

      // Refresh claims once
      try {
        const idTokenResult = await user.getIdTokenResult(true);
        setIsAdmin(idTokenResult.claims.admin === true);
      } catch (error) {
        console.error('Error checking admin claim:', error);
        setIsAdmin(false);
      }

      // Subscribe to root user doc
      try {
        const userRef = doc(db, 'users', user.uid);
        unsubUser = onSnapshot(
          userRef,
          (snap) => setUserDoc(snap.exists() ? snap.data() : null),
          (err) => {
            console.error('Error reading user doc:', err);
            setUserDoc(null);
          }
        );
      } catch (error) {
        console.error('Error subscribing to user doc:', error);
        setUserDoc(null);
      }

      // Subscribe to settings/main doc (access + lifetime)
      try {
        const settingsRef = doc(db, 'users', user.uid, 'settings', 'main');
        unsubSettings = onSnapshot(
          settingsRef,
          (snap) => setSettingsDoc(snap.exists() ? snap.data() : null),
          (err) => {
            console.error('Error reading settings doc:', err);
            setSettingsDoc(null);
          }
        );
      } catch (error) {
        console.error('Error subscribing to settings doc:', error);
        setSettingsDoc(null);
      }

      // Mark loading false once we’ve attached listeners (snap callbacks will fill data)
      setLoading(false);
    });

    return () => {
      if (unsubUser) unsubUser();
      if (unsubSettings) unsubSettings();
      unsubscribeAuth();
    };
  }, []);

  const logout = () => signOut(auth);

  // Root doc convenience
  const plan = userDoc?.plan || 'free';
  const subscriptionStatus = userDoc?.subscriptionStatus || 'inactive';
  const role = userDoc?.role || 'user';

  // Settings doc convenience (what Layout/paywall usually cares about)
  const access = settingsDoc?.access || 'locked';
  const lifetime = settingsDoc?.lifetime === true;

  const value = useMemo(
    () => ({
      currentUser,
      isAdmin,

      userDoc,
      settingsDoc,

      role,
      plan,
      subscriptionStatus,

      access,
      lifetime,

      loading,
      logout,
    }),
    [
      currentUser,
      isAdmin,
      userDoc,
      settingsDoc,
      role,
      plan,
      subscriptionStatus,
      access,
      lifetime,
      loading,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



